// Updated OTPVerification component
import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation, Navigate } from 'react-router-dom';
import { RefreshCw } from 'lucide-react';
import { ReactComponent as Logo } from "../../assects/svg/auth/Triva_Logo.svg";
import { ReactComponent as Trivaillusion } from "../../assects/svg/auth/Triva_illusion.svg";
import { useAuth } from '../../context/auth/AuthContext';
import { verifyOtp } from '../../services/auth';
import { ApiError } from '../../services/http';
import { Button } from '../Ui_Components/Button';

const OTPVerification = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();
  const email = location.state?.email;
  const nutritionistId = location.state?.nutritionistId;
  
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [isVerifying, setIsVerifying] = useState(false);
  const [error, setError] = useState('');
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  
  useEffect(() => {
    if (email) {
      inputRefs.current[0]?.focus();
    }
  }, [email]);
  
  const maskEmail = (email: string): string => {
    const [localPart, domain] = email.split('@');
    if (localPart.length <= 3) {
      return `${localPart}@${domain}`;
    }
    const hiddenCharsCount = localPart.length - 3;
    const asterisks = '*'.repeat(hiddenCharsCount);
    return `${localPart.substring(0, 3)}${asterisks}@${domain}`;
  };

  const handleOtpChange = (index: number, value: string) => {
    if (value.length <= 1 && /^\d*$/.test(value)) {
      const newOtp = [...otp];
      newOtp[index] = value;
      setOtp(newOtp);
      setError('');

      if (value && index < 5) {
        inputRefs.current[index + 1]?.focus();
      }
    }
  };

  const handleOtpKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace') {
      if (!otp[index] && index > 0) {
        inputRefs.current[index - 1]?.focus();
      } else if (otp[index]) {
        const newOtp = [...otp];
        newOtp[index] = '';
        setOtp(newOtp);
      }
    }
    
    if (e.key === 'Enter') {
      const isComplete = otp.every((digit) => digit !== '');
      if (isComplete) {
        handleVerify();
      }
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text/plain').trim();
    const digits = pastedData.replace(/\D/g, '').slice(0, 6);
    
    if (digits.length > 0) {
      const newOtp = [...otp];
      
      for (let i = 0; i < 6; i++) {
        newOtp[i] = digits[i] || '';
      }
      
      setOtp(newOtp);
      
      const nextEmptyIndex = newOtp.findIndex(digit => digit === '');
      if (nextEmptyIndex !== -1) {
        inputRefs.current[nextEmptyIndex]?.focus();
      } else {
        inputRefs.current[5]?.focus();
      }
    }
  };

  const handleVerify = async () => {
    if (otp.every((digit) => digit !== '')) {
      const otpString = otp.join('');
      
      setIsVerifying(true);
      setError('');
      
      try {
        // verifyOtp persists access/refresh tokens internally on success, so
        // the profile fetch triggered by login() below carries the bearer.
        const { nutritionistData } = await verifyOtp(otpString, nutritionistId ?? '');
        login(nutritionistData);
        navigate('/dashboard', { replace: true });
      } catch (error) {
        if (error instanceof ApiError) {
          setError(error.message);
        } else {
          setError('Verification error. Please try again.');
        }
        setIsVerifying(false);
        console.error('OTP Verification Error:', error);
      }
    }
  };

  const handleResend = () => {
    console.log('Resending code...');
    setOtp(['', '', '', '', '', '']);
    setError('');
    inputRefs.current[0]?.focus();
  };

  const handleBack = () => {
    navigate('/signin');
  };

  const isOtpComplete = otp.every((digit) => digit !== '');
  
  if (!email) {
    return <Navigate to="/signin" replace />;
  }
  
  const maskedEmail = maskEmail(email);

  return (
    <div className="min-h-screen bg-[#FFFFFF] flex items-center justify-center p-4 relative">
      <div className="absolute top-8 right-8">
        <div className="flex items-center gap-2">
          <Logo className="h-10 w-auto" />
        </div>
      </div>

      <div className="absolute bottom-8 left-8">
        <Trivaillusion className="h-36 w-auto" />
      </div>

      <div className="w-full max-w-[520px] bg-white rounded-xl border-[0.5px] border-[#2F2E38] shadow-sm p-6 sm:p-8">
        <button
          onClick={handleBack}
          className="mb-4 text-sm text-gray-600 hover:text-gray-900 flex items-center gap-1 transition-colors"
        >
          ← Back to sign in
        </button>

        <h1 className="text-2xl font-semibold text-gray-900 mb-2">
          OTP Verification
        </h1>
        
        <p className="text-sm text-gray-600 mb-6">
         Verification code sent to email{' '}
          <span className="text-gray-900 font-medium">{maskedEmail}</span>
        </p>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-normal text-gray-700 mb-3">
              Type your 6 digits security code
            </label>
            <div className="flex gap-3 mb-4">
              {otp.map((digit, index) => (
                <input
                  key={index}
                  ref={(el) => { inputRefs.current[index] = el; }}
                  id={`otp-${index}`}
                  type="text"
                  inputMode="numeric"
                  maxLength={1}
                  value={digit}
                  onChange={(e) => handleOtpChange(index, e.target.value)}
                  onKeyDown={(e) => handleOtpKeyDown(index, e)}
                  onPaste={handlePaste}
                  disabled={isVerifying}
                  className={`w-12 h-12 text-center text-lg font-medium bg-white border rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-400 focus:border-transparent transition-all ${
                    error ? 'border-red-500' : 'border-gray-300'
                  } ${isVerifying ? 'opacity-50 cursor-not-allowed' : ''}`}
                />
              ))}
            </div>
            {error && (
              <p className="text-xs text-red-500 mb-2">{error}</p>
            )}
          </div>

          <div>
            <span className="text-sm text-gray-600">Didn't get the code? </span>
            <button
              onClick={handleResend}
              disabled={isVerifying}
              className="text-sm text-teal-500 hover:text-teal-600 font-normal disabled:opacity-50"
            >
              Resend
            </button>
          </div>

          <Button
            variant="primary"
            size="lg"
            fullWidth
            onClick={handleVerify}
            disabled={!isOtpComplete || isVerifying}
            className="mt-6"
          >
            {isVerifying ? (
              <>
                <RefreshCw className="w-4 h-4 animate-spin" />
                Verifying...
              </>
            ) : (
              'Verify'
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default OTPVerification;